import { useState, useRef, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Button } from '@/components/ui/button';
import { Camera, Image, RefreshCw, SwitchCamera } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CameraCaptureProps {
  onCapture: (imageSrc: string) => void;
  label: string;
  className?: string;
  disabled?: boolean;
  facingMode?: "user" | "environment";
}

const CameraCapture = ({ onCapture, label, className, disabled, facingMode = "environment" }: CameraCaptureProps) => {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [currentFacingMode, setCurrentFacingMode] = useState(facingMode);
  const webcamRef = useRef<Webcam>(null);

  const handleStartCamera = () => {
    setShowWebcam(true);
  };

  const handleSwitchCamera = () => {
    setCurrentFacingMode(prev => prev === "user" ? "environment" : "user");
  };

  const capturePhoto = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) {
        setCapturedImage(imageSrc);
        onCapture(imageSrc);
        setShowWebcam(false);
      }
    }
  }, [onCapture, webcamRef]);

  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setShowWebcam(true);
  }, []);

  return (
    <div className={cn("flex flex-col space-y-2", className)}>
      <label className="text-sm font-medium">{label}</label>

      <div className="relative aspect-video overflow-hidden rounded-md border border-input bg-background">
        {!showWebcam && !capturedImage && (
          <div className="flex h-full flex-col items-center justify-center">
            <Image className="h-12 w-12 text-muted-foreground mb-2" />
            <Button onClick={handleStartCamera} variant="outline" disabled={disabled}>
              <Camera className="h-4 w-4 mr-2" />
              Tomar Foto
            </Button>
          </div>
        )}

        {showWebcam && !capturedImage && (
          <div className="relative h-full">
            <Webcam
              key={currentFacingMode} // Forzar el re-montaje del componente al cambiar la cámara
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={{ facingMode: currentFacingMode }}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-4 flex justify-center items-center space-x-4">
              {/* Botón para cambiar de cámara */}
              <Button
                onClick={handleSwitchCamera}
                size="icon"
                variant="secondary"
                className="h-10 w-10 rounded-full bg-black/50 text-white"
              >
                <SwitchCamera className="h-5 w-5" />
              </Button>

              {/* Botón para capturar la foto */}
              <Button
                onClick={capturePhoto}
                size="icon"
                className="h-12 w-12 rounded-full bg-white text-primary shadow-lg"
                disabled={disabled}
              >
                <Camera className="h-6 w-6" />
              </Button>
               {/* Placeholder para mantener el balance, o puedes agregar otro botón a la derecha si es necesario */}
              <div className="h-10 w-10" />
            </div>
          </div>
        )}

        {capturedImage && (
          <div className="relative h-full">
            <img
              src={capturedImage}
              className="h-full w-full object-cover"
              alt="Captured"
            />
            <div className="absolute bottom-2 right-2">
              <Button
                onClick={retakePhoto}
                size="sm"
                variant="secondary"
                disabled={disabled}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Volver a tomar
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CameraCapture;
